import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { API_URL } from './utils/authUtils';
import { devLogSafe, devLog, devError } from './utils/secureLogger';

const BACKEND_URL = API_URL;

// Helper function to construct full S3 URL
const getImageUrl = (imagePath) => {
  if (!imagePath) return null;
  
  // If already a full URL, return as-is
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }
  
  // Construct S3 URL for scholars-uploads bucket
  const S3_BASE_URL = 'https://scholars-uploads.s3.us-east-2.amazonaws.com';
  return `${S3_BASE_URL}/${imagePath}`;
};

const RecruiterDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [holdErrors, setHoldErrors] = useState({}); // Per-student hold errors
  const [holdSuccesses, setHoldSuccesses] = useState({}); // Per-student hold success messages
  const [holdFeatureEnabled, setHoldFeatureEnabled] = useState(false); // Hold feature toggle
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  
  // Get recruiter ID from localStorage
  const [recruiterId, setRecruiterId] = useState(null);
  const [isAdminMode, setIsAdminMode] = useState(false);
  const [isBoardLiaisonMode, setIsBoardLiaisonMode] = useState(false);
  const [liaisonHasWriteAccess, setLiaisonHasWriteAccess] = useState(false);
  
  // Student categories
  const [interestedStudents, setInterestedStudents] = useState([]);
  const [appliedStudents, setAppliedStudents] = useState([]);
  const [matchedStudents, setMatchedStudents] = useState([]);
  const [debugInfo, setDebugInfo] = useState(null);
  const [recruiterInfo, setRecruiterInfo] = useState(null);

  // Pagination state for infinite scroll (students per page)
  const STUDENTS_PER_PAGE = 50;
  const [shortlistedPage, setShortlistedPage] = useState(1);
  const [interestedPage, setInterestedPage] = useState(1);
  const [appliedPage, setAppliedPage] = useState(1);
  const [matchedPage, setMatchedPage] = useState(1);

  // Server-side pagination metadata (totals for summary stats)
  const [paginationMeta, setPaginationMeta] = useState({
    interested_total: 0,
    applied_total: 0,
    matched_total: 0,
    has_more_interested: false,
    has_more_applied: false,
    has_more_matched: false,
  });
  const [loadingMore, setLoadingMore] = useState({
    interested: false,
    applied: false,
    matched: false,
  });

  // Refs for infinite scroll sentinel elements
  const shortlistedSentinelRef = useRef(null);
  const interestedSentinelRef = useRef(null);
  const appliedSentinelRef = useRef(null);
  const matchedSentinelRef = useRef(null);

  // Shortlist management
  const [shortlistedIds, setShortlistedIds] = useState(new Set());
  const [expandedStudents, setExpandedStudents] = useState(new Set());
  
  // Notes management
  const [notesExpanded, setNotesExpanded] = useState(new Set());
  const [candidateNotes, setCandidateNotes] = useState({});
  const [notesTimeout, setNotesTimeout] = useState({});
  
  // Workflow states from recruiter_applicant_interactions
  const [interactionStates, setInteractionStates] = useState({});
  
  // Application link management
  const [applicationLink, setApplicationLink] = useState('');
  const [editingLink, setEditingLink] = useState(false);
  const [savingLink, setSavingLink] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [tempApplicationLink, setTempApplicationLink] = useState('');
  const [pendingStudentId, setPendingStudentId] = useState(null);
  
  // Category collapse state - default to expanded (true)
  const [categoriesExpanded, setCategoriesExpanded] = useState({
    interested: true,
    applied: true,
    matched: true,
    shortlisted: true,
    recruiterRequirements: false
  });

  // Recruiter Requirements field expansion state
  const [requirementFieldsExpanded, setRequirementFieldsExpanded] = useState({
    institutions: false,
    majors: false,
    classYears: false
  });

  // Sticky navigation state
  const [activeSection, setActiveSection] = useState('');

  // Action Required modal state
  const [showActionModal, setShowActionModal] = useState(false);

  // Blog access state
  const [blogAccessEnabled, setBlogAccessEnabled] = useState(false);
  const [blogAccessLoading, setBlogAccessLoading] = useState(true);

  // Recruiter dashboard field visibility
  const [showStatusField, setShowStatusField] = useState(true);
  const [showEligibleField, setShowEligibleField] = useState(true);

  // Matching candidates anonymization
  const [anonymizationEnabled, setAnonymizationEnabled] = useState(true);

  // Offer management state
  const [showOfferModal, setShowOfferModal] = useState(false);
  const [selectedStudentForOffer, setSelectedStudentForOffer] = useState(null);
  const [offerData, setOfferData] = useState({
    position_title: '',
    position_description: '',
    compensation: '',
    start_date: '',
    duration: '',
    response_deadline: '',
    custom_message: ''
  });
  const [sendingOffer, setSendingOffer] = useState(false);

  // Hold management state
  const [placingHold, setPlacingHold] = useState(false);
  const [releasingHold, setReleasingHold] = useState(false);
  const [extendingHold, setExtendingHold] = useState(false);

  // Infinite scroll handler using IntersectionObserver
  const useInfiniteScroll = (sentinelRef, hasMore, loadMore) => {
    useEffect(() => {
      const sentinel = sentinelRef.current;
      if (!sentinel || !hasMore) return;

      const observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) {
            loadMore();
          }
        },
        { threshold: 0.1 }
      );

      observer.observe(sentinel);

      return () => {
        if (sentinel) {
          observer.unobserve(sentinel);
        }
      };
    }, [sentinelRef, hasMore, loadMore]);
  };

  useEffect(() => {
    // Check for admin or board liaison viewing mode
    const urlParams = new URLSearchParams(window.location.search);
    const adminView = urlParams.get('adminView');
    const boardLiaisonView = urlParams.get('boardLiaisonView');
    const targetRecruiterId = urlParams.get('recruiterId');
    
    if (adminView === 'true' && targetRecruiterId) {
      // Admin viewing mode
      const adminToken = localStorage.getItem('token');
      if (adminToken) {
        setIsAdminMode(true);
        setRecruiterId(parseInt(targetRecruiterId));

        // Fetch recruiter info for display using admin endpoint
        const fetchRecruiterInfo = async () => {
          try {
            const response = await axios.get(
              `${BACKEND_URL}/api/recruiter/profile/${targetRecruiterId}`,
              {
                headers: {
                  Authorization: `Bearer ${adminToken}`
                }
              }
            );
            setRecruiterInfo(response.data);
          } catch (error) {
            console.error('Error fetching recruiter info:', error);
          }
        };

        fetchRecruiterInfo();
        return; // Prevent fall-through to normal recruiter auth check
      }
    } else if (boardLiaisonView === 'true' && targetRecruiterId) {
      // Board liaison viewing mode
      const boardLiaisonToken = localStorage.getItem('boardLiaisonToken');
      const boardLiaisonInfo = localStorage.getItem('boardLiaisonInfo');

      if (boardLiaisonToken && boardLiaisonInfo) {
        try {
          const liaisonData = JSON.parse(boardLiaisonInfo);
          const hasWriteAccess = liaisonData.can_write === true;

          setIsAdminMode(true); // Use admin mode UI but with liaison permissions
          setIsBoardLiaisonMode(true); // Track that this is board liaison mode
          setLiaisonHasWriteAccess(hasWriteAccess); // Track write permission
          setRecruiterId(parseInt(targetRecruiterId));

          console.log('[Board Liaison Mode] Write access:', hasWriteAccess);

          // Data will be fetched by the main fetchDashboardData function
          return;
        } catch (error) {
          console.error('Error parsing board liaison info:', error);
        }
      }
    }
    
    // Normal recruiter mode
    const recruiterInfo = localStorage.getItem('recruiterInfo');
    
    if (!recruiterInfo) {
      // No recruiter info, redirect to login
      navigate('/recruiter/login');
      return;
    }
    
    try {
      const recruiter = JSON.parse(recruiterInfo);
      setRecruiterId(recruiter.id);
      setRecruiterInfo(recruiter);
    } catch (error) {
      console.error('Error parsing recruiter info:', error);
      navigate('/recruiter/login');
      return;
    }
  }, [navigate]);

  useEffect(() => {
    if (recruiterId) {
      fetchDashboardData();
      fetchBlogAccessSetting();
      fetchHoldFeatureSetting();
      fetchDashboardFieldSettings();
      
      // Check if this is the first time showing the dashboard (after login)
      const hasSeenUpdatePrompt = localStorage.getItem('hasSeenUpdatePrompt');
      if (!hasSeenUpdatePrompt) {
        setShowUpdateModal(true);
        localStorage.setItem('hasSeenUpdatePrompt', 'true');
      }
    }
  }, [recruiterId]);

  // Cleanup effect for notes timeouts
  useEffect(() => {
    return () => {
      // Clear all timeouts when component unmounts
      Object.values(notesTimeout).forEach(timeoutId => {
        if (timeoutId) clearTimeout(timeoutId);
      });
    };
  }, []);

  // Scroll detection for sticky nav active section highlighting
  useEffect(() => {
    const handleScroll = () => {
      const sections = [
        { id: 'shortlisted-section', name: 'shortlisted' },
        { id: 'favorited-you-section', name: 'favorited' },
        { id: 'applied-section', name: 'applied' },
        { id: 'matching-section', name: 'matching' }
      ];

      const scrollPosition = window.scrollY + 150; // Offset for sticky nav height

      for (const section of sections) {
        const element = document.getElementById(section.id);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section.name);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Call once on mount

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Infinite scroll setup for each section (server-side pagination)
  // Track current page for each category based on loaded students
  const currentInterestedPage = Math.ceil(interestedStudents.length / STUDENTS_PER_PAGE) || 1;
  const currentAppliedPage = Math.ceil(appliedStudents.length / STUDENTS_PER_PAGE) || 1;
  const currentMatchedPage = Math.ceil(matchedStudents.length / STUDENTS_PER_PAGE) || 1;

  useInfiniteScroll(
    interestedSentinelRef,
    paginationMeta.has_more_interested && !loadingMore.interested,
    () => {
      setInterestedPage(prev => prev + 1);
      loadMoreStudents('interested', currentInterestedPage);
    }
  );

  useInfiniteScroll(
    appliedSentinelRef,
    paginationMeta.has_more_applied && !loadingMore.applied,
    () => {
      setAppliedPage(prev => prev + 1);
      loadMoreStudents('applied', currentAppliedPage);
    }
  );

  useInfiniteScroll(
    matchedSentinelRef,
    paginationMeta.has_more_matched && !loadingMore.matched,
    () => {
      setMatchedPage(prev => prev + 1);
      loadMoreStudents('matched', currentMatchedPage);
    }
  );

  // Shortlisted section has subsections, calculate total shortlisted
  const totalShortlisted = [...interestedStudents, ...appliedStudents, ...matchedStudents]
    .filter(s => shortlistedIds.has(s.id)).length;

  useInfiniteScroll(
    shortlistedSentinelRef,
    totalShortlisted > shortlistedPage * STUDENTS_PER_PAGE,
    () => setShortlistedPage(prev => prev + 1)
  );

  // Action Required modal logic - check for non-shortlisted candidates
  useEffect(() => {
    // Don't show if Update Requisition modal is currently showing (priority)
    if (showUpdateModal) {
      return;
    }

    // Only show if there are candidates that need attention
    const hasNonShortlistedFavorited = interestedStudents.some(
      student => !shortlistedIds.has(student.id)
    );
    const hasNonShortlistedApplied = appliedStudents.some(
      student => !shortlistedIds.has(student.id) &&
                !interestedStudents.some(interested => interested.id === student.id)
    );

    if (hasNonShortlistedFavorited || hasNonShortlistedApplied) {
      // Check if user dismissed it recently (within 24 hours)
      const dismissedTimestamp = localStorage.getItem('actionModalDismissed');
      if (dismissedTimestamp) {
        const timeSinceDismissal = Date.now() - parseInt(dismissedTimestamp);
        const twentyFourHours = 24 * 60 * 60 * 1000;

        if (timeSinceDismissal < twentyFourHours) {
          // Still within 24 hour period, don't show modal
          return;
        }
      }

      // Show the modal
      setShowActionModal(true);
    }
  }, [interestedStudents, appliedStudents, shortlistedIds, showUpdateModal]);

  // Fetch blog access setting
  const fetchBlogAccessSetting = async () => {
    try {
      setBlogAccessLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/admin/settings/blog_access_enabled`);
      setBlogAccessEnabled(response.data.setting_value === 'true');
    } catch (error) {
      devError('Failed to fetch blog access setting, defaulting to false:', error);
      setBlogAccessEnabled(false);
    } finally {
      setBlogAccessLoading(false);
    }
  };

  // Navigate to blog admin
  const handleBlogManagement = () => {
    navigate('/blog-admin');
  };

  const fetchHoldFeatureSetting = async () => {
    try {
      // Try to get the setting from the public endpoint
      const response = await axios.get(
        `${BACKEND_URL}/api/admin/settings/hold_feature_enabled/public`
      );
      const settingValue = response.data.setting_value === 'true';
      setHoldFeatureEnabled(settingValue);
      devLog('Hold feature enabled:', settingValue);
    } catch (err) {
      console.error('Error fetching hold feature setting:', err);
      // Default to false if there's an error
      setHoldFeatureEnabled(false);
    }
  };

  const fetchDashboardFieldSettings = async () => {
    try {
      const statusResponse = await axios.get(
        `${BACKEND_URL}/api/admin/settings/recruiter_dashboard_show_status/public`
      );
      setShowStatusField(statusResponse.data.setting_value === 'true');

      const eligibleResponse = await axios.get(
        `${BACKEND_URL}/api/admin/settings/recruiter_dashboard_show_eligible/public`
      );
      setShowEligibleField(eligibleResponse.data.setting_value === 'true');

      const anonymizationResponse = await axios.get(
        `${BACKEND_URL}/api/admin/settings/matching_candidates_anonymized/public`
      );
      setAnonymizationEnabled(anonymizationResponse.data.setting_value === 'true');
    } catch (err) {
      console.error('Error fetching dashboard field settings:', err);
      // Default to true if there's an error
      setShowStatusField(true);
      setShowEligibleField(true);
      setAnonymizationEnabled(true);
    }
  };

  const fetchDashboardData = async () => {
    devLog('fetchDashboardData called, recruiterId:', recruiterId);
    if (!recruiterId) {
      console.error('No recruiter ID available');
      return;
    }
    
    setLoading(true);
    try {
      // Get authentication token and endpoint based on mode
      let token, endpoint;
      
      if (isBoardLiaisonMode) {
        // Board liaison viewing mode
        token = localStorage.getItem('boardLiaisonToken');
        endpoint = `${BACKEND_URL}/api/board-liaison/view-recruiter-dashboard/${recruiterId}`;
      } else if (isAdminMode) {
        // Admin viewing mode
        token = localStorage.getItem('token');
        endpoint = `${BACKEND_URL}/api/admin/view-recruiter-dashboard/${recruiterId}`;
      } else {
        // Regular recruiter mode
        token = localStorage.getItem('recruiterToken') || localStorage.getItem('token');
        endpoint = `${BACKEND_URL}/api/recruiter/dashboard/${recruiterId}`;
      }
      
      if (!token) {
        console.error('No authentication token found');
        if (isBoardLiaisonMode) {
          window.location.href = '/board-liaison/login';
        } else if (isAdminMode) {
          navigate('/admin');
        } else {
          navigate('/recruiter/login');
        }
        return;
      }
      
      devLog('Making dashboard API call...');
      const response = await axios.get(endpoint, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        params: {
          page: 1,
          per_page: STUDENTS_PER_PAGE
        }
      });
      devLogSafe('Dashboard API response received', response.data);

      // Categorize students
      setInterestedStudents(response.data.interested_students || []);
      setAppliedStudents(response.data.applied_students || []);
      setMatchedStudents(response.data.matched_students || []);

      // Store pagination metadata for summary stats and load-more logic
      if (response.data.pagination) {
        setPaginationMeta({
          interested_total: response.data.pagination.interested_total || 0,
          applied_total: response.data.pagination.applied_total || 0,
          matched_total: response.data.pagination.matched_total || 0,
          has_more_interested: response.data.pagination.has_more_interested || false,
          has_more_applied: response.data.pagination.has_more_applied || false,
          has_more_matched: response.data.pagination.has_more_matched || false,
        });
      }

      // Debug: Check if hold status is being received
      const sampleStudent = response.data.applied_students?.[0] || response.data.interested_students?.[0];
      console.log('Dashboard data received:', {
        interested_count: response.data.interested_students?.length || 0,
        applied_count: response.data.applied_students?.length || 0,
        matched_count: response.data.matched_students?.length || 0,
        interested_total: response.data.pagination?.interested_total || 0,
        applied_total: response.data.pagination?.applied_total || 0,
        matched_total: response.data.pagination?.matched_total || 0,
        hold_count: response.data.recruiter_info?.hold_count,
        hold_limit: response.data.recruiter_info?.hold_limit,
        sample_student: sampleStudent,
        sample_hold_status: sampleStudent?.hold_status
      });
      
      // Load shortlisted IDs
      const shortlisted = new Set(response.data.shortlisted_ids || []);
      setShortlistedIds(shortlisted);
      
      // Load interaction states
      setInteractionStates(response.data.interaction_states || {});
      
      // Load notes from interaction states
      const notes = {};
      Object.keys(response.data.interaction_states || {}).forEach(studentId => {
        const state = response.data.interaction_states[studentId];
        if (state && state.notes) {
          notes[studentId] = state.notes;
        }
      });
      setCandidateNotes(notes);
      
      // Load debug info
      setDebugInfo(response.data.debug_info || null);
      
      // Load recruiter info
      setRecruiterInfo(response.data.recruiter_info || null);
      devLogSafe('Dashboard API Response', response.data);
      devLogSafe('Recruiter Info', response.data.recruiter_info);
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      
      // Handle authentication errors
      if (err.response?.status === 401 || err.response?.status === 403) {
        console.error('Authentication failed, redirecting to login');
        
        if (isBoardLiaisonMode) {
          localStorage.removeItem('boardLiaisonToken');
          localStorage.removeItem('boardLiaisonInfo');
          window.location.href = '/board-liaison/login';
        } else if (isAdminMode) {
          localStorage.removeItem('token');
          navigate('/admin');
        } else {
          localStorage.removeItem('recruiterToken');
          localStorage.removeItem('recruiterInfo');
          navigate('/recruiter/login');
        }
      } else {
        setError('Failed to load dashboard data');
      }
    } finally {
      setLoading(false);
    }
  };

  // Load more students for a specific category (server-side pagination)
  const loadMoreStudents = async (category, currentPage) => {
    if (!recruiterId) return;

    // Prevent duplicate requests
    if (loadingMore[category]) return;

    setLoadingMore(prev => ({ ...prev, [category]: true }));

    try {
      let token, endpoint;

      if (isBoardLiaisonMode) {
        token = localStorage.getItem('boardLiaisonToken');
        endpoint = `${BACKEND_URL}/api/board-liaison/view-recruiter-dashboard/${recruiterId}`;
      } else if (isAdminMode) {
        token = localStorage.getItem('token');
        endpoint = `${BACKEND_URL}/api/admin/view-recruiter-dashboard/${recruiterId}`;
      } else {
        token = localStorage.getItem('recruiterToken') || localStorage.getItem('token');
        endpoint = `${BACKEND_URL}/api/recruiter/dashboard/${recruiterId}`;
      }

      if (!token) return;

      const nextPage = currentPage + 1;
      const response = await axios.get(endpoint, {
        headers: { 'Authorization': `Bearer ${token}` },
        params: { page: nextPage, per_page: STUDENTS_PER_PAGE }
      });

      // Append new students to existing arrays (avoid duplicates by ID)
      const existingIds = {
        interested: new Set(interestedStudents.map(s => s.id)),
        applied: new Set(appliedStudents.map(s => s.id)),
        matched: new Set(matchedStudents.map(s => s.id)),
      };

      if (response.data.interested_students) {
        const newInterested = response.data.interested_students.filter(s => !existingIds.interested.has(s.id));
        if (newInterested.length > 0) {
          setInterestedStudents(prev => [...prev, ...newInterested]);
        }
      }

      if (response.data.applied_students) {
        const newApplied = response.data.applied_students.filter(s => !existingIds.applied.has(s.id));
        if (newApplied.length > 0) {
          setAppliedStudents(prev => [...prev, ...newApplied]);
        }
      }

      if (response.data.matched_students) {
        const newMatched = response.data.matched_students.filter(s => !existingIds.matched.has(s.id));
        if (newMatched.length > 0) {
          setMatchedStudents(prev => [...prev, ...newMatched]);
        }
      }

      // Update pagination metadata
      if (response.data.pagination) {
        setPaginationMeta(prev => ({
          ...prev,
          has_more_interested: response.data.pagination.has_more_interested || false,
          has_more_applied: response.data.pagination.has_more_applied || false,
          has_more_matched: response.data.pagination.has_more_matched || false,
        }));
      }

      // Update interaction states with new data
      if (response.data.interaction_states) {
        setInteractionStates(prev => ({ ...prev, ...response.data.interaction_states }));
      }

      console.log(`Loaded page ${nextPage} for ${category}:`, {
        interested_new: response.data.interested_students?.length || 0,
        applied_new: response.data.applied_students?.length || 0,
        matched_new: response.data.matched_students?.length || 0,
      });

    } catch (err) {
      console.error(`Error loading more ${category} students:`, err);
    } finally {
      setLoadingMore(prev => ({ ...prev, [category]: false }));
    }
  };

  const toggleShortlist = async (studentId) => {
    if (!recruiterId) {
      console.error('No recruiter ID available');
      return;
    }
    
    try {
      const isCurrentlyShortlisted = shortlistedIds.has(studentId);
      
      const response = await axios.post(`${BACKEND_URL}/api/recruiter/shortlist`, {
        recruiter_id: recruiterId,
        applicant_id: studentId,
        action: isCurrentlyShortlisted ? 'remove' : 'add'
      });

      if (response.data.success) {
        const newShortlisted = new Set(shortlistedIds);
        if (isCurrentlyShortlisted) {
          newShortlisted.delete(studentId);
        } else {
          newShortlisted.add(studentId);
        }
        setShortlistedIds(newShortlisted);
        
        // Refresh interaction states
        fetchDashboardData();
      }
    } catch (err) {
      console.error('Error toggling shortlist:', err);
      setError('Failed to update shortlist');
    }
  };

  const sendNotification = async (studentId, notificationType) => {
    if (!recruiterId) {
      console.error('No recruiter ID available');
      return;
    }

    // Check if trying to send external link without application link set
    if (notificationType === 'external_link' && !recruiterInfo?.external_application_link) {
      setPendingStudentId(studentId);
      setShowLinkModal(true);
      return;
    }
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/recruiter/send-notification`, {
        recruiter_id: recruiterId,
        applicant_id: studentId,
        notification_type: notificationType
      });

      if (response.data.success) {
        const messages = {
          'apply_invite': 'profile request sent successfully!',
          'external_link': 'external link sent successfully!',
          'interview_invite': 'interview invite sent successfully!'
        };
        setSuccessMessage(messages[notificationType] || `${notificationType} sent successfully!`);
        setTimeout(() => setSuccessMessage(''), 3000);
        fetchDashboardData(); // Refresh to update button states
      }
    } catch (err) {
      console.error('Error sending notification:', err);
      setError(`Failed to send ${notificationType}`);
    }
  };

  const saveApplicationLink = async () => {
    if (!applicationLink.trim()) {
      setError('Please enter a valid application link');
      return;
    }

    if (!applicationLink.startsWith('http://') && !applicationLink.startsWith('https://')) {
      setError('Application link must start with http:// or https://');
      return;
    }

    try {
      setSavingLink(true);
      setError('');
      
      const response = await axios.put(`${BACKEND_URL}/api/recruiter/update-application-link`, {
        recruiter_id: recruiterId,
        application_link: applicationLink
      });

      if (response.data.success) {
        // Update recruiter info
        setRecruiterInfo(prev => ({
          ...prev,
          external_application_link: applicationLink
        }));
        
        // Update localStorage
        const storedInfo = JSON.parse(localStorage.getItem('recruiterInfo') || '{}');
        storedInfo.external_application_link = applicationLink;
        localStorage.setItem('recruiterInfo', JSON.stringify(storedInfo));
        
        setEditingLink(false);
        setSuccessMessage('Application link saved successfully!');
        setTimeout(() => setSuccessMessage(''), 3000);
      }
    } catch (err) {
      console.error('Error saving application link:', err);
      setError(err.response?.data?.detail || 'Failed to save application link');
    } finally {
      setSavingLink(false);
    }
  };

  const saveAndContinue = async () => {
    if (!tempApplicationLink.trim()) {
      setError('Please enter a valid application link');
      return;
    }

    if (!tempApplicationLink.startsWith('http://') && !tempApplicationLink.startsWith('https://')) {
      setError('Application link must start with http:// or https://');
      return;
    }

    try {
      setSavingLink(true);
      setError('');
      
      const response = await axios.put(`${BACKEND_URL}/api/recruiter/update-application-link`, {
        recruiter_id: recruiterId,
        application_link: tempApplicationLink
      });

      if (response.data.success) {
        // Update recruiter info
        setRecruiterInfo(prev => ({
          ...prev,
          external_application_link: tempApplicationLink
        }));
        
        // Update localStorage
        const storedInfo = JSON.parse(localStorage.getItem('recruiterInfo') || '{}');
        storedInfo.external_application_link = tempApplicationLink;
        localStorage.setItem('recruiterInfo', JSON.stringify(storedInfo));
        
        // Close modal
        setShowLinkModal(false);
        setTempApplicationLink('');
        
        // Now send the external link to the pending student
        if (pendingStudentId) {
          await sendNotification(pendingStudentId, 'external_link');
          setPendingStudentId(null);
        }
        
        setSuccessMessage('Application link saved and external link sent!');
        setTimeout(() => setSuccessMessage(''), 3000);
      }
    } catch (err) {
      console.error('Error saving application link:', err);
      setError(err.response?.data?.detail || 'Failed to save application link');
    } finally {
      setSavingLink(false);
    }
  };

  const toggleCategoryExpanded = (category) => {
    setCategoriesExpanded(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const toggleRequirementField = (field) => {
    setRequirementFieldsExpanded(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  // Smooth scroll to section
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const yOffset = -80; // Offset for sticky nav bar
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  // Handle Action Required modal dismissal
  const handleActionModalDismiss = () => {
    localStorage.setItem('actionModalDismissed', Date.now().toString());
    setShowActionModal(false);
  };

  // Helper function to render truncated lists with show more/less
  const renderTruncatedList = (itemsString, field, displayLimit = 3) => {
    if (!itemsString || itemsString === 'Not specified') {
      return <p className="text-gray-600 dark:text-gray-400">Not specified</p>;
    }

    // Parse the string into an array (handle both comma and semicolon separators)
    const separator = itemsString.includes(';') ? ';' : ',';
    const items = itemsString.split(separator).map(item => item.trim()).filter(item => item);
    const totalCount = items.length;

    // If less than or equal to displayLimit, just show all
    if (totalCount <= displayLimit) {
      return <p className="text-gray-600 dark:text-gray-400 break-words">{itemsString}</p>;
    }

    // Show truncated or full list based on expansion state
    const isExpanded = requirementFieldsExpanded[field];
    const displayItems = isExpanded ? items : items.slice(0, displayLimit);
    const remainingCount = totalCount - displayLimit;

    return (
      <div className="space-y-1">
        <p className="text-gray-600 dark:text-gray-400 break-words">
          {displayItems.join(', ')}
          {!isExpanded && <span className="text-gray-500 dark:text-gray-500"> ... +{remainingCount} more</span>}
        </p>
        <button
          onClick={(e) => {
            e.stopPropagation();
            toggleRequirementField(field);
          }}
          className="text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 font-medium"
        >
          {isExpanded ? '▲ Show less' : `▼ Show all (${totalCount})`}
        </button>
      </div>
    );
  };

  // Calculate non-shortlisted candidates for action modal
  const nonShortlistedFavorited = interestedStudents.filter(
    student => !shortlistedIds.has(student.id)
  );

  const nonShortlistedApplied = appliedStudents.filter(
    student => !shortlistedIds.has(student.id) &&
               !interestedStudents.some(interested => interested.id === student.id)
  );

  const toggleStudentExpansion = (studentId) => {
    const newExpanded = new Set(expandedStudents);
    if (newExpanded.has(studentId)) {
      newExpanded.delete(studentId);
    } else {
      newExpanded.add(studentId);
    }
    setExpandedStudents(newExpanded);
  };

  const toggleNotesExpanded = (studentId) => {
    const newExpanded = new Set(notesExpanded);
    if (newExpanded.has(studentId)) {
      newExpanded.delete(studentId);
    } else {
      newExpanded.add(studentId);
    }
    setNotesExpanded(newExpanded);
  };

  const updateNotes = async (studentId, notes) => {
    if (!recruiterId) {
      console.error('No recruiter ID available');
      return;
    }
    
    // Update local state immediately
    setCandidateNotes(prev => ({
      ...prev,
      [studentId]: notes
    }));
    
    // Clear existing timeout
    if (notesTimeout[studentId]) {
      clearTimeout(notesTimeout[studentId]);
    }
    
    // Set new timeout for auto-save
    const timeoutId = setTimeout(async () => {
      try {
        devLog(`Saving notes for student ${studentId}:`, notes);
        const response = await axios.put(`${BACKEND_URL}/api/recruiter/notes/${recruiterId}/${studentId}`, {
          notes: notes
        });
        devLogSafe(`Notes saved for student ${studentId}`, response.data);
      } catch (err) {
        console.error('Error saving notes:', err);
        console.error('Error details:', err.response?.data);
        setError('Failed to save notes');
      }
    }, 1000); // Auto-save after 1 second of no typing
    
    setNotesTimeout(prev => ({
      ...prev,
      [studentId]: timeoutId
    }));
  };

  const handleLogout = () => {
    // Clear recruiter authentication data
    localStorage.removeItem('recruiterToken');
    localStorage.removeItem('recruiterInfo');
    localStorage.removeItem('hasSeenUpdatePrompt');
    
    // Redirect to login page
    navigate('/recruiter/login');
  };

  // Offer management functions
  const openOfferModal = (student) => {
    setSelectedStudentForOffer(student);
    setOfferData({
      position_title: '',
      position_description: '',
      compensation: '',
      start_date: '',
      duration: '',
      response_deadline: '',
      custom_message: ''
    });
    setShowOfferModal(true);
  };

  const closeOfferModal = () => {
    setShowOfferModal(false);
    setSelectedStudentForOffer(null);
    setSendingOffer(false);
  };

  const handleOfferInputChange = (field, value) => {
    setOfferData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const sendOffer = async () => {
    if (!selectedStudentForOffer || !recruiterId) {
      setError('Missing required information to send offer');
      return;
    }

    if (!offerData.position_title.trim()) {
      setError('Position title is required');
      return;
    }

    try {
      setSendingOffer(true);
      setError('');

      // Prepare token and payload
      const token = isBoardLiaisonMode
        ? localStorage.getItem('boardLiaisonToken')
        : localStorage.getItem('recruiterToken');

      const payload = {
        student_id: selectedStudentForOffer.id,
        position_title: offerData.position_title,
        position_description: offerData.position_description,
        compensation: offerData.compensation,
        start_date: offerData.start_date || null,
        duration: offerData.duration,
        response_deadline: offerData.response_deadline || null,
        custom_message: offerData.custom_message
      };

      if (isBoardLiaisonMode) {
        payload.recruiter_id = recruiterId; // Board liaisons must specify which recruiter
      }

      const response = await axios.post(
        `${BACKEND_URL}/api/recruiter/offers/send`,
        payload,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.data) {
        setSuccessMessage(`Offer sent successfully to ${selectedStudentForOffer.first_name} ${selectedStudentForOffer.last_name}!`);
        setTimeout(() => setSuccessMessage(''), 3000);
        closeOfferModal();
        fetchDashboardData(); // Refresh to update interaction states
      }
    } catch (err) {
      console.error('Error sending offer:', err);
      setError(err.response?.data?.detail || 'Failed to send offer');
    } finally {
      setSendingOffer(false);
    }
  };

  // Hold management functions
  const placeHold = async (studentId) => {
    // Disable for admin mode, or board liaison mode without write access
    if (isAdminMode && !isBoardLiaisonMode) return;
    if (isBoardLiaisonMode && !liaisonHasWriteAccess) return;

    try {
      setPlacingHold(true);
      // Clear any existing error/success for this student
      setHoldErrors(prev => ({ ...prev, [studentId]: '' }));
      setHoldSuccesses(prev => ({ ...prev, [studentId]: '' }));

      // Prepare request payload and token
      const token = isBoardLiaisonMode
        ? localStorage.getItem('boardLiaisonToken')
        : localStorage.getItem('recruiterToken');

      const payload = { student_id: studentId };
      if (isBoardLiaisonMode) {
        payload.recruiter_id = recruiterId; // Board liaisons must specify which recruiter
      }

      const response = await axios.post(
        `${BACKEND_URL}/api/recruiter/holds`,
        payload,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.data) {
        // Set per-student success message
        setHoldSuccesses(prev => ({ 
          ...prev, 
          [studentId]: `Hold placed successfully! (${response.data.days_remaining} days remaining)` 
        }));
        // Clear after 5 seconds
        setTimeout(() => {
          setHoldSuccesses(prev => ({ ...prev, [studentId]: '' }));
        }, 5000);
        fetchDashboardData(); // Refresh to update hold states
      }
    } catch (err) {
      console.error('Error placing hold:', err);
      // Set per-student error message
      setHoldErrors(prev => ({ 
        ...prev, 
        [studentId]: err.response?.data?.detail || 'Failed to place hold' 
      }));
      // Clear after 7 seconds
      setTimeout(() => {
        setHoldErrors(prev => ({ ...prev, [studentId]: '' }));
      }, 7000);
    } finally {
      setPlacingHold(false);
    }
  };

  const releaseHold = async (studentId) => {
    // Disable for admin mode, or board liaison mode without write access
    if (isAdminMode && !isBoardLiaisonMode) return;
    if (isBoardLiaisonMode && !liaisonHasWriteAccess) return;

    try {
      setReleasingHold(true);
      // Clear any existing error/success for this student
      setHoldErrors(prev => ({ ...prev, [studentId]: '' }));
      setHoldSuccesses(prev => ({ ...prev, [studentId]: '' }));

      // Prepare token and URL
      const token = isBoardLiaisonMode
        ? localStorage.getItem('boardLiaisonToken')
        : localStorage.getItem('recruiterToken');

      const url = isBoardLiaisonMode
        ? `${BACKEND_URL}/api/recruiter/holds/${studentId}?recruiter_id=${recruiterId}`
        : `${BACKEND_URL}/api/recruiter/holds/${studentId}`;

      const response = await axios.delete(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.data.success) {
        // Set per-student success message
        setHoldSuccesses(prev => ({ 
          ...prev, 
          [studentId]: 'Hold released successfully!' 
        }));
        // Clear after 5 seconds
        setTimeout(() => {
          setHoldSuccesses(prev => ({ ...prev, [studentId]: '' }));
        }, 5000);
        fetchDashboardData(); // Refresh to update hold states
      }
    } catch (err) {
      console.error('Error releasing hold:', err);
      // Set per-student error message
      setHoldErrors(prev => ({ 
        ...prev, 
        [studentId]: err.response?.data?.detail || 'Failed to release hold' 
      }));
      // Clear after 7 seconds
      setTimeout(() => {
        setHoldErrors(prev => ({ ...prev, [studentId]: '' }));
      }, 7000);
    } finally {
      setReleasingHold(false);
    }
  };

  const extendHold = async (studentId) => {
    // Disable for admin mode, or board liaison mode without write access
    if (isAdminMode && !isBoardLiaisonMode) return;
    if (isBoardLiaisonMode && !liaisonHasWriteAccess) return;

    try {
      setExtendingHold(true);
      setError('');

      // Prepare token and URL
      const token = isBoardLiaisonMode
        ? localStorage.getItem('boardLiaisonToken')
        : localStorage.getItem('recruiterToken');

      const url = isBoardLiaisonMode
        ? `${BACKEND_URL}/api/recruiter/holds/${studentId}/extend?recruiter_id=${recruiterId}`
        : `${BACKEND_URL}/api/recruiter/holds/${studentId}/extend`;

      const response = await axios.put(url, {}, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.data.success) {
        setSuccessMessage('Hold extended successfully! (7 additional days)');
        setTimeout(() => setSuccessMessage(''), 3000);
        fetchDashboardData(); // Refresh to update hold states
      }
    } catch (err) {
      console.error('Error extending hold:', err);
      setError(err.response?.data?.detail || 'Failed to extend hold');
    } finally {
      setExtendingHold(false);
    }
  };

  const renderStudent = (student, category) => {
    const isShortlisted = shortlistedIds.has(student.id);
    const isExpanded = expandedStudents.has(student.id);
    const interactions = interactionStates[student.id] || {};
    const isAnonymous = category === 'matched' && anonymizationEnabled;
    const holdStatus = student.hold_status || {};
    
    // Debug logging for hold status
    if (student.id && (category === 'applied' || category === 'interested')) {
      console.log(`Hold status for student ${student.id} (${student.first_name} ${student.last_name}):`, {
        category,
        holdStatus,
        isAdminMode,
        isBoardLiaisonMode,
        hold_status_exists: !!student.hold_status,
        is_on_hold: holdStatus.is_on_hold,
        held_by_current: holdStatus.held_by_current_recruiter,
        canPlaceHold_calculated: (category === 'applied' || category === 'interested') &&
                                !holdStatus.is_on_hold &&
                                (!isAdminMode || (isBoardLiaisonMode && liaisonHasWriteAccess)) &&
                                (!isBoardLiaisonMode || liaisonHasWriteAccess)
      });
    }

    // Check if actions should be disabled due to holds
    const isHeldByOther = holdStatus.is_on_hold && !holdStatus.held_by_current_recruiter;
    // Allow holds for: regular recruiters, OR board liaisons with write access
    const canPlaceHold = holdFeatureEnabled &&
                        (category === 'applied' || category === 'interested') &&
                        !holdStatus.is_on_hold &&
                        (!isAdminMode || (isBoardLiaisonMode && liaisonHasWriteAccess)) &&
                        (!isBoardLiaisonMode || liaisonHasWriteAccess);

    // Helper function to render verification badge
    const renderVerificationBadge = (metadata) => {
      if (!metadata) return null;

      const { data_source, verified_at, application_date } = metadata;

      // Determine badge type
      if (data_source === 'verified') {
        if (verified_at && application_date && new Date(verified_at) > new Date(application_date)) {
          // Partner verified after application
          return (
            <span className="inline-flex items-center px-2 py-0.5 text-xs rounded bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400" title="Verified by university partner">
              ✓ Verified
            </span>
          );
        }
        return null; // Using verified data but no recent verification
      } else if (data_source === 'current') {
        // Using current/self-reported data
        return (
          <span className="inline-flex items-center px-2 py-0.5 text-xs rounded bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400" title="Self-reported by student">
            ⏳ Self-reported
          </span>
        );
      }

      return null;
    };

    // Helper function to render field with optional strikethrough of old value
    const renderFieldWithVerification = (metadata, fieldName) => {
      if (!metadata || !metadata[fieldName]) {
        return null;
      }

      const field = metadata[fieldName];
      const showStrikethrough = field.source === 'current' &&
                               field.verified_value &&
                               field.current_value &&
                               field.verified_value !== field.current_value;

      if (showStrikethrough) {
        return (
          <span>
            <span className="line-through text-gray-400 dark:text-gray-600 mr-2">{field.verified_value}</span>
            <span>{field.current_value}</span>
          </span>
        );
      }

      return <span>{field.value}</span>;
    };

    return (
      <div key={student.id} className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-3 sm:p-4 mb-4">
        {/* Mobile Layout */}
        <div className="md:hidden">
          {/* Basic Info */}
          <div className="flex items-center gap-2 mb-3">
            {!isAnonymous && (
              <div className="w-12 h-12 rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center overflow-hidden flex-shrink-0">
                {student.headshot ? (
                  <img 
                    src={getImageUrl(student.headshot)} 
                    alt={`${student.first_name} ${student.last_name}`}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      console.error('Image failed to load:', getImageUrl(student.headshot));
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                    onLoad={() => {
                      devLog('Image loaded successfully:', getImageUrl(student.headshot));
                    }}
                  />
                ) : null}
                <div 
                  className={`w-full h-full flex items-center justify-center text-gray-500 dark:text-gray-400 font-semibold ${student.headshot ? 'hidden' : 'flex'}`}
                >
                  {student.first_name ? student.first_name[0] : '?'}{student.last_name ? student.last_name[0] : ''}
                </div>
              </div>
            )}
            <div className="min-w-0 flex-1">
              <h3 className="text-base font-semibold text-gray-900 dark:text-white truncate">
                {isAnonymous ? `Candidate #${student.id}` : `${student.first_name} ${student.last_name}`}
              </h3>
              <p className="text-xs text-gray-600 dark:text-gray-400 line-clamp-2">
                {student.current_university_verified} •{' '}
                {student.verification_metadata?.major ?
                  renderFieldWithVerification(student.verification_metadata, 'major') :
                  student.major_verified
                }{' '}
                • Class of: {student.current_academic_class_verified}
              </p>
            </div>
          </div>

          {/* Key Metrics - Stacked on Mobile */}
          <div className="flex flex-col space-y-2 mb-4 text-xs">
            <div className="flex justify-between items-center">
              <span className="font-medium text-gray-700 dark:text-gray-300">GPA:</span>
              <div className="flex items-center gap-2">
                {student.verification_metadata?.gpa ?
                  renderFieldWithVerification(student.verification_metadata, 'gpa') :
                  <span>{student.gpa_verified || 'N/A'}</span>
                }
                {student.verification_metadata?.gpa && renderVerificationBadge(student.verification_metadata)}
              </div>
            </div>
            {showStatusField && (
              <div className="flex justify-between">
                <span className="font-medium text-gray-700 dark:text-gray-300">Status:</span>
                <span className={`${student.partner_has_verified ? 'text-green-600' : 'text-yellow-600'}`}>
                  {student.partner_has_verified ? 'Verified' : 'Pending'}
                </span>
              </div>
            )}
            {showEligibleField && (
              <div className="flex justify-between">
                <span className="font-medium text-gray-700 dark:text-gray-300">Eligible:</span>
                <span className={`${student.eligible_sp25 ? 'text-green-600' : 'text-yellow-600'}`}>
                  {student.eligible_sp25 ? 'Yes' : 'Pending'}
                </span>
              </div>
            )}
            {holdFeatureEnabled && holdStatus.is_on_hold && (
              <div className="flex justify-between">
                <span className="font-medium text-gray-700 dark:text-gray-300">Hold:</span>
                <span className={`text-xs px-1 py-0.5 rounded ${
                  holdStatus.held_by_current_recruiter 
                    ? 'bg-blue-100 text-blue-800' 
                    : holdStatus.held_by_same_company 
                      ? 'bg-purple-100 text-purple-800'
                      : 'bg-red-100 text-red-800'
                }`}>
                  {holdStatus.held_by_current_recruiter 
                    ? `You (${holdStatus.days_remaining}d)` 
                    : holdStatus.held_by_same_company 
                      ? `${holdStatus.holder_name} (${holdStatus.days_remaining}d)`
                      : `On Hold (${holdStatus.days_remaining}d)`
                  }
                </span>
              </div>
            )}
{/* Location Display */}
            {(student.city || student.state || student.zip) && (
              <div className="flex justify-between">
                <span className="font-medium text-gray-700 dark:text-gray-300">Location:</span>
                <span className="text-gray-600">
                  {[student.city, student.state, student.zip].filter(Boolean).join(', ')}
                </span>
              </div>
            )}
            {category === 'interested' && (
              <div className="flex justify-between">
                <span className="font-medium text-gray-700 dark:text-gray-300">Interest:</span>
                <span className="text-green-600">✓</span>
              </div>
            )}
          </div>

          {/* Mobile Actions */}
          <div className="flex flex-wrap gap-2 mt-3">
            {/* Shortlist Toggle */}
            <button
              onClick={isAdminMode === true ? undefined : () => toggleShortlist(student.id)}
              disabled={isAdminMode === true}
              className={`px-2 py-1 text-xs rounded transition-colors ${
                isAdminMode === true 
                  ? 'opacity-50 cursor-not-allowed bg-gray-100 text-gray-500'
                  : isShortlisted 
                    ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {isShortlisted ? '★' : '☆'}
              {isAdminMode === true && ' (RO)'}
            </button>

            {/* Hold Management Buttons */}
            {canPlaceHold && (
              <button
                onClick={() => {
                  console.log(`Hold button clicked for student ${student.id}`);
                  placeHold(student.id);
                }}
                disabled={placingHold}
                className="px-2 py-1 text-xs rounded transition-colors bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 relative group"
                style={{ minWidth: '60px' }}
                title="If you hold and release a candidate, you must wait 7 days before you can hold them again"
              >
                {placingHold ? '⏳' : '🔒'} Hold
                {/* Tooltip on hover */}
                <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-900 rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap max-w-xs z-10 hidden group-hover:block">
                  ⚠️ If you hold and release a candidate, you must wait 7 days before you can hold them again
                </span>
              </button>
            )}
            
            {holdFeatureEnabled && holdStatus.held_by_current_recruiter && (
              <>
                <button
                  onClick={() => releaseHold(student.id)}
                  disabled={releasingHold}
                  className="px-2 py-1 text-xs rounded transition-colors bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                >
                  {releasingHold ? '⏳' : '🔓'} Release
                </button>
                
                {holdFeatureEnabled && holdStatus.can_extend && !holdStatus.extended && (
                  <button
                    onClick={() => extendHold(student.id)}
                    disabled={extendingHold}
                    className="px-2 py-1 text-xs rounded transition-colors bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
                  >
                    {extendingHold ? '⏳' : '⏰'} +7d
                  </button>
                )}
              </>
            )}

            {/* Mobile workflow buttons - Only show if shortlisted */}
            {isShortlisted && (
              <>
                {category === 'matched' && !interactions.apply_invite_sent_at && (
                  <button
                    onClick={isAdminMode === true || isHeldByOther ? undefined : () => sendNotification(student.id, 'apply_invite')}
                    disabled={isAdminMode === true || isHeldByOther}
                    className={`px-2 py-1 text-xs rounded transition-colors ${
                      isAdminMode === true || isHeldByOther
                        ? 'opacity-50 cursor-not-allowed bg-gray-100 text-gray-500'
                        : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                  >
                    Request Profile{isAdminMode === true && ' (RO)'}{isHeldByOther && ' (Held)'}
                  </button>
                )}

                {(category === 'interested' || category === 'applied') && !interactions.external_link_sent_at && (
                  <button
                    onClick={isAdminMode === true || isHeldByOther ? undefined : () => sendNotification(student.id, 'external_link')}
                    disabled={isAdminMode === true || isHeldByOther}
                    className={`px-2 py-1 text-xs rounded transition-colors ${
                      isAdminMode === true || isHeldByOther
                        ? 'opacity-50 cursor-not-allowed bg-gray-100 text-gray-500'
                        : 'bg-green-600 text-white hover:bg-green-700'
                    }`}
                  >
                    Link{isAdminMode === true && ' (RO)'}{isHeldByOther && ' (Held)'}
                  </button>
                )}

                {(category === 'interested' || category === 'applied') && interactions.external_link_sent_at && !interactions.interview_invite_sent_at && (
                  <button
                    onClick={isAdminMode === true || isHeldByOther ? undefined : () => sendNotification(student.id, 'interview_invite')}
                    disabled={isAdminMode === true || isHeldByOther}
                    className={`px-2 py-1 text-xs rounded transition-colors ${
                      isAdminMode === true || isHeldByOther
                        ? 'opacity-50 cursor-not-allowed bg-gray-100 text-gray-500'
                        : 'bg-purple-600 text-white hover:bg-purple-700'
                    }`}
                  >
                    Queue{isAdminMode === true && ' (RO)'}{isHeldByOther && ' (Held)'}
                  </button>
                )}

                {(category === 'interested' || category === 'applied') && interactions.external_link_sent_at && !interactions.offer_sent && (
                  <button
                    onClick={(isAdminMode && !isBoardLiaisonMode) || (isBoardLiaisonMode && !liaisonHasWriteAccess) || isHeldByOther ? undefined : () => openOfferModal(student)}
                    disabled={isAdminMode === true || isHeldByOther}
                    className={`px-2 py-1 text-xs rounded transition-colors ${
                      isAdminMode === true || isHeldByOther
                        ? 'opacity-50 cursor-not-allowed bg-gray-100 text-gray-500'
                        : 'bg-orange-600 text-white hover:bg-orange-700'
                    }`}
                  >
                    🎯{isAdminMode === true && ' (RO)'}{isHeldByOther && ' (Held)'}
                  </button>
                )}

                {isShortlisted && (
                  <button
                    onClick={isAdminMode === true ? undefined : () => toggleNotesExpanded(student.id)}
                    disabled={isAdminMode === true}
                    className={`px-2 py-1 text-xs rounded transition-colors ${
                      isAdminMode === true
                        ? 'opacity-50 cursor-not-allowed bg-gray-100 text-gray-500'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    📝{candidateNotes[student.id] ? '●' : ''}
                  </button>
                )}
              </>
            )}
          </div>

          {/* Hold Error/Success Messages - Mobile */}
          {(holdErrors[student.id] || holdSuccesses[student.id]) && (
            <div className={`mt-3 p-2 rounded-md text-xs ${
              holdErrors[student.id] 
                ? 'bg-red-100 text-red-700 border border-red-300' 
                : 'bg-green-100 text-green-700 border border-green-300'
            }`}>
              <div className="flex items-start gap-2">
                <span className="flex-shrink-0">
                  {holdErrors[student.id] ? '⚠️' : '✅'}
                </span>
                <span>{holdErrors[student.id] || holdSuccesses[student.id]}</span>
              </div>
            </div>
          )}
        </div>

        {/* Desktop Layout - Only show on screens 768px and above */}
        <div className="hidden md:flex md:justify-between md:items-start gap-3">
          <div className="flex-1">
            {/* Basic Info */}
            <div className="flex items-center gap-3 mb-2">
              {!isAnonymous && (
                <div className="w-16 h-16 rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center overflow-hidden flex-shrink-0">
                  {student.headshot ? (
                    <img 
                      src={getImageUrl(student.headshot)} 
                      alt={`${student.first_name} ${student.last_name}`}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        console.error('Image failed to load:', getImageUrl(student.headshot));
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                      onLoad={() => {
                        devLog('Image loaded successfully:', getImageUrl(student.headshot));
                      }}
                    />
                  ) : null}
                  <div 
                    className={`w-full h-full flex items-center justify-center text-gray-500 dark:text-gray-400 font-semibold ${student.headshot ? 'hidden' : 'flex'}`}
                  >
                    {student.first_name ? student.first_name[0] : '?'}{student.last_name ? student.last_name[0] : ''}
                  </div>
                </div>
              )}
              <div className="min-w-0 flex-1">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white truncate">
                  {isAnonymous ? `Candidate #${student.id}` : `${student.first_name} ${student.last_name}`}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
                  {student.current_university_verified} •{' '}
                  {student.verification_metadata?.major ?
                    renderFieldWithVerification(student.verification_metadata, 'major') :
                    student.major_verified
                  }{' '}
                  • Class of: {student.current_academic_class_verified}
                </p>
              </div>
            </div>

            {/* Key Metrics - Desktop only uses flex layout, no grid */}
            <div className="flex flex-wrap gap-x-6 gap-y-2 mb-3 text-sm">
              <div className="flex items-center gap-2">
                <span className="font-medium text-gray-700 dark:text-gray-300">GPA:</span>
                {student.verification_metadata?.gpa ?
                  renderFieldWithVerification(student.verification_metadata, 'gpa') :
                  <span className="ml-1">{student.gpa_verified || 'N/A'}</span>
                }
                {student.verification_metadata?.gpa && renderVerificationBadge(student.verification_metadata)}
              </div>
              {showStatusField && (
                <div className="truncate">
                  <span className="font-medium text-gray-700 dark:text-gray-300">Status:</span>
                  <span className={`ml-1 ${student.partner_has_verified ? 'text-green-600' : 'text-yellow-600'}`}>
                    {student.partner_has_verified ? 'Verified' : 'Pending'}
                  </span>
                </div>
              )}
              {showEligibleField && (
                <div className="truncate">
                  <span className="font-medium text-gray-700 dark:text-gray-300">Eligible:</span>
                  <span className={`ml-1 ${student.eligible_sp25 ? 'text-green-600' : 'text-yellow-600'}`}>
                    {student.eligible_sp25 ? 'Yes' : 'Pending'}
                  </span>
                </div>
              )}
              {/* Location Display */}
              {(student.city || student.state || student.zip) && (
                <div className="truncate">
                  <span className="font-medium text-gray-700 dark:text-gray-300">Location:</span>
                  <span className="ml-1 text-gray-600">
                    {[student.city, student.state, student.zip].filter(Boolean).join(', ')}
                  </span>
                </div>
              )}
              {category === 'interested' && (
                <div className="truncate">
                  <span className="font-medium text-gray-700 dark:text-gray-300">Interest:</span>
                  <span className="ml-1 text-green-600">✓</span>
                </div>
              )}
              {holdFeatureEnabled && holdStatus.is_on_hold && (
                <div className="truncate">
                  <span className="font-medium text-gray-700 dark:text-gray-300">Hold:</span>
                  <span className={`ml-1 text-xs px-2 py-1 rounded ${
                    holdStatus.held_by_current_recruiter 
                      ? 'bg-blue-100 text-blue-800' 
                      : holdStatus.held_by_same_company 
                        ? 'bg-purple-100 text-purple-800'
                        : 'bg-red-100 text-red-800'
                  }`}>
                    {holdStatus.held_by_current_recruiter 
                      ? `You (${holdStatus.days_remaining}d)` 
                      : holdStatus.held_by_same_company 
                        ? `${holdStatus.holder_name} (${holdStatus.days_remaining}d)`
                        : `On Hold (${holdStatus.days_remaining}d)`
                    }
                  </span>
                </div>
              )}
            </div>

            {/* Offer Status - Show for all shortlisted candidates */}
            {isShortlisted && (
              <div className="mb-3 p-3 rounded-lg bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium text-gray-700 dark:text-gray-300">Made Offer:</span>
                    <span className={`ml-1 font-semibold ${interactions.offer_sent ? 'text-orange-600' : 'text-gray-500'}`}>
                      {interactions.offer_sent ? '✓ Yes' : '✗ No'}
                    </span>
                    {interactions.offer_sent && interactions.offer_sent_at && (
                      <div className="mt-1 text-xs text-gray-600 dark:text-gray-400">
                        Offer sent: {new Date(interactions.offer_sent_at).toLocaleDateString()}
                      </div>
                    )}
                  </div>
                  {interactions.offer_sent && (
                    <div>
                      <span className="font-medium text-gray-700 dark:text-gray-300">Offer Status:</span>
                      <span className={`ml-1 font-semibold ${
                        interactions.offer_status === 'accepted' ? 'text-green-600' : 
                        interactions.offer_status === 'declined' ? 'text-red-600' : 
                        'text-yellow-600'
                      }`}>
                        {interactions.offer_status === 'accepted' ? '✅ Accepted' :
                         interactions.offer_status === 'declined' ? '❌ Declined' : 
                         '⏳ Pending Response'}
                      </span>
                      {interactions.offer_status && interactions.offer_status !== 'pending' && interactions.offer_response_at && (
                        <div className="mt-1 text-xs text-gray-600 dark:text-gray-400">
                          Offer {interactions.offer_status}: {new Date(interactions.offer_response_at).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Expanded Content */}
            {isExpanded && !isAnonymous && interactions.external_link_sent_at && (
              <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-700 rounded">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <p className="font-medium text-gray-700 dark:text-gray-300">Email:</p>
                    <p className="text-gray-600 dark:text-gray-400">{student.university_email || student.personal_email}</p>
                  </div>
                  {student.resume && (
                    <div>
                      <p className="font-medium text-gray-700 dark:text-gray-300">Resume:</p>
                      <a 
                        href={student.resume} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-700 underline flex items-center gap-1"
                        onClick={() => devLog('Opening resume:', student.resume)}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        {student.resume_filename ? `View ${student.resume_filename}` : 'View Resume'}
                      </a>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Actions for Desktop */}
          <div className="flex flex-col gap-2 ml-4">
            {/* Shortlist Toggle */}
            <button
              onClick={isAdminMode === true ? undefined : () => toggleShortlist(student.id)}
              disabled={isAdminMode === true}
              className={`px-2 sm:px-3 py-1 sm:py-1.5 text-xs sm:text-sm rounded transition-colors flex-1 sm:flex-none ${
                isAdminMode === true 
                  ? 'opacity-50 cursor-not-allowed bg-gray-100 text-gray-500'
                  : isShortlisted 
                    ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <span className="sm:hidden">{isShortlisted ? '★' : '☆'}</span>
              <span className="hidden sm:inline">{isShortlisted ? '★ Shortlisted' : '☆ Shortlist'}</span>
              {isAdminMode === true && <span className="hidden sm:inline"> (Read-only)</span>}
            </button>

            {/* Hold Management Buttons */}
            {canPlaceHold && (
              <button
                onClick={() => {
                  console.log(`Hold button clicked for student ${student.id}`);
                  placeHold(student.id);
                }}
                disabled={placingHold}
                className="px-3 py-1.5 text-sm rounded transition-colors bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 relative group"
                style={{ minWidth: '120px' }}
                title="If you hold and release a candidate, you must wait 7 days before you can hold them again"
              >
                {placingHold ? '⏳ Placing...' : '🔒 Hold Student'}
                {/* Tooltip on hover */}
                <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 text-sm text-white bg-gray-900 rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-normal max-w-xs z-10 hidden group-hover:block shadow-lg">
                  ⚠️ If you hold and release a candidate, you must wait 7 days before you can hold them again
                </span>
              </button>
            )}
            
            {holdFeatureEnabled && holdStatus.held_by_current_recruiter && (
              <>
                <button
                  onClick={() => releaseHold(student.id)}
                  disabled={releasingHold}
                  className="px-3 py-1.5 text-sm rounded transition-colors bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                >
                  {releasingHold ? '⏳ Releasing...' : '🔓 Release Hold'}
                </button>
                
                {holdFeatureEnabled && holdStatus.can_extend && !holdStatus.extended && (
                  <button
                    onClick={() => extendHold(student.id)}
                    disabled={extendingHold}
                    className="px-3 py-1.5 text-sm rounded transition-colors bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
                  >
                    {extendingHold ? '⏳ Extending...' : '⏰ Extend +7 Days'}
                  </button>
                )}
              </>
            )}

            {/* Workflow Buttons - Only show if shortlisted */}
            {isShortlisted && (
              <>
                {/* For matched (not applied) students */}
                {category === 'matched' && !interactions.apply_invite_sent_at && (
                  <button
                    onClick={isAdminMode === true || isHeldByOther ? undefined : () => sendNotification(student.id, 'apply_invite')}
                    disabled={isAdminMode === true || isHeldByOther}
                    className={`px-3 py-1.5 text-sm rounded transition-colors ${
                      isAdminMode === true || isHeldByOther
                        ? 'opacity-50 cursor-not-allowed bg-gray-100 text-gray-500'
                        : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                  >
                    Request Student Profile{isAdminMode === true && ' (Read-only)'}{isHeldByOther && ' (On Hold)'}
                  </button>
                )}

                {/* For applied students (interested or not) */}
                {(category === 'interested' || category === 'applied') && (
                  <>
                    {!interactions.external_link_sent_at && (
                      <button
                        onClick={isAdminMode === true || isHeldByOther ? undefined : () => sendNotification(student.id, 'external_link')}
                        disabled={isAdminMode === true || isHeldByOther}
                        className={`px-3 py-1.5 text-sm rounded transition-colors ${
                          isAdminMode === true || isHeldByOther
                            ? 'opacity-50 cursor-not-allowed bg-gray-100 text-gray-500'
                            : 'bg-green-600 text-white hover:bg-green-700'
                        }`}
                      >
                        Send External Link{isAdminMode === true && ' (Read-only)'}{isHeldByOther && ' (On Hold)'}
                      </button>
                    )}

                    {interactions.external_link_sent_at && !interactions.interview_invite_sent_at && (
                      <button
                        onClick={isAdminMode === true || isHeldByOther ? undefined : () => sendNotification(student.id, 'interview_invite')}
                        disabled={isAdminMode === true || isHeldByOther}
                        className={`px-3 py-1.5 text-sm rounded transition-colors ${
                          isAdminMode === true || isHeldByOther
                            ? 'opacity-50 cursor-not-allowed bg-gray-100 text-gray-500'
                            : 'bg-purple-600 text-white hover:bg-purple-700'
                        }`}
                      >
                        Add to Interview Queue{isAdminMode === true && ' (Read-only)'}{isHeldByOther && ' (On Hold)'}
                      </button>
                    )}

                    {/* Make Offer Button - Show after interview invite sent or if external link sent for flexible workflow */}
                    {interactions.external_link_sent_at && !interactions.offer_sent && (
                      <button
                        onClick={(isAdminMode && !isBoardLiaisonMode) || (isBoardLiaisonMode && !liaisonHasWriteAccess) || isHeldByOther ? undefined : () => openOfferModal(student)}
                        disabled={isAdminMode === true || isHeldByOther}
                        className={`px-3 py-1.5 text-sm rounded transition-colors ${
                          isAdminMode === true || isHeldByOther
                            ? 'opacity-50 cursor-not-allowed bg-gray-100 text-gray-500'
                            : 'bg-orange-600 text-white hover:bg-orange-700'
                        }`}
                      >
                        🎯 Make Offer{isAdminMode === true && ' (Read-only)'}{isHeldByOther && ' (On Hold)'}
                      </button>
                    )}
                  </>
                )}

                {/* Read receipts */}
                {interactions.apply_invite_sent_at && (
                  <span className="text-xs text-gray-500">
                    Profile request sent {interactions.apply_invite_read_at ? '✓✓' : '✓'}
                  </span>
                )}
                {interactions.external_link_sent_at && (
                  <span className="text-xs text-gray-500">
                    Link sent {interactions.external_link_read_at ? '✓✓' : '✓'}
                  </span>
                )}
                {interactions.interview_invite_sent_at && (
                  <span className="text-xs text-gray-500">
                    Added to Queue {interactions.interview_invite_read_at ? '✓✓' : '✓'}
                  </span>
                )}
                {interactions.offer_sent && (
                  <span className={`text-xs ${
                    interactions.offer_status === 'accepted' ? 'text-green-600' : 
                    interactions.offer_status === 'declined' ? 'text-red-600' : 
                    'text-yellow-600'
                  }`}>
                    Offer {interactions.offer_status || 'sent'} {
                      interactions.offer_status === 'accepted' ? '✅' :
                      interactions.offer_status === 'declined' ? '❌' : '📧'
                    }
                  </span>
                )}
              </>
            )}

            {/* Notes Button - Only show if shortlisted */}
            {isShortlisted && (
              <button
                onClick={isAdminMode === true ? undefined : () => toggleNotesExpanded(student.id)}
                disabled={isAdminMode === true}
                className={`px-3 py-1.5 text-sm rounded transition-colors flex items-center gap-1 ${
                  isAdminMode === true
                    ? 'opacity-50 cursor-not-allowed bg-gray-100 text-gray-500'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                📝 Notes {candidateNotes[student.id] ? '●' : ''}{isAdminMode === true && ' (Read-only)'}
              </button>
            )}

            {/* Expand/Collapse for non-anonymous students with external link sent */}
            {!isAnonymous && interactions.external_link_sent_at && (
              <button
                onClick={() => toggleStudentExpansion(student.id)}
                className="px-3 py-1 text-xs text-gray-600 hover:text-gray-800"
              >
                {isExpanded ? '▼ Less' : '▶ More'}
              </button>
            )}
          </div>
        </div>

        {/* Hold Error/Success Messages - Desktop */}
        {(holdErrors[student.id] || holdSuccesses[student.id]) && (
          <div className={`mt-3 p-3 rounded-md text-sm ${
            holdErrors[student.id] 
              ? 'bg-red-50 text-red-700 border border-red-200' 
              : 'bg-green-50 text-green-700 border border-green-200'
          }`}>
            <div className="flex items-center gap-2">
              <span className="flex-shrink-0 text-lg">
                {holdErrors[student.id] ? '⚠️' : '✅'}
              </span>
              <span className="font-medium">{holdErrors[student.id] || holdSuccesses[student.id]}</span>
            </div>
          </div>
        )}
        
        {/* Notes Section - Only show for shortlisted candidates when expanded */}
        {isShortlisted && notesExpanded.has(student.id) && (
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-600">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Notes for {isAnonymous ? `Candidate #${student.id}` : `${student.first_name} ${student.last_name}`}:
            </label>
            <textarea
              value={candidateNotes[student.id] || ''}
              onChange={(e) => updateNotes(student.id, e.target.value)}
              placeholder="Add notes about this candidate... (auto-saves)"
              className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              rows={3}
            />
            <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
              Notes are automatically saved and visible only to you.
            </div>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white dark:from-gray-900 dark:to-gray-800 p-8">
        <div className="max-w-7xl mx-auto">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-8"></div>
            <div className="space-y-4">
              <div className="h-32 bg-gray-200 dark:bg-gray-700 rounded"></div>
              <div className="h-32 bg-gray-200 dark:bg-gray-700 rounded"></div>
              <div className="h-32 bg-gray-200 dark:bg-gray-700 rounded"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white dark:from-gray-900 dark:to-gray-800">
      {/* Update Requisition Modal */}
      {showUpdateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex justify-between items-start mb-4">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Need to update your requisition?
              </h2>
              <button
                onClick={() => setShowUpdateModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <p className="text-gray-600 dark:text-gray-300 mb-6">
              Keep your internship requisition up to date to attract the best candidates. Update your requirements, locations, and internship details.
            </p>

            <div className="flex gap-4">
              <button
                onClick={() => {
                  setShowUpdateModal(false);
                  navigate('/recruiter-signup');
                }}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Update Now
              </button>
              <button
                onClick={() => setShowUpdateModal(false)}
                className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 dark:bg-gray-600 dark:text-gray-300 rounded-lg hover:bg-gray-400 dark:hover:bg-gray-500 transition-colors"
              >
                Later
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Action Required Modal - Non-Shortlisted Candidates */}
      {showActionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl max-w-2xl w-full p-6">
            <div className="flex justify-between items-start mb-6">
              <div className="flex items-center gap-3">
                <div className="bg-gradient-to-r from-purple-500 to-pink-600 p-2 rounded-lg">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                  Action Required
                </h2>
              </div>
              <button
                onClick={handleActionModalDismiss}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <p className="text-gray-700 dark:text-gray-300 mb-6 text-lg">
              You have candidates waiting for your review. Shortlist them to access their resumes and continue the recruitment process.
            </p>

            <div className="space-y-4 mb-6">
              {/* Favorited You Alert */}
              {nonShortlistedFavorited.length > 0 && (
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border-l-4 border-green-500 p-4 rounded-lg">
                  <div className="flex items-start gap-3">
                    <svg className="w-6 h-6 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                    </svg>
                    <div className="flex-1">
                      <h3 className="font-semibold text-green-900 dark:text-green-100 mb-1">
                        {nonShortlistedFavorited.length} {nonShortlistedFavorited.length === 1 ? 'Candidate Has' : 'Candidates Have'} Favorited You
                      </h3>
                      <p className="text-green-800 dark:text-green-200 text-sm mb-3">
                        These candidates are interested in your company. Review and shortlist them to access their resumes.
                      </p>
                      <button
                        onClick={() => {
                          scrollToSection('favorited-you-section');
                          handleActionModalDismiss();
                        }}
                        className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors shadow-md hover:shadow-lg"
                      >
                        Review Favorited Candidates →
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Applied Students Alert */}
              {nonShortlistedApplied.length > 0 && (
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-l-4 border-blue-500 p-4 rounded-lg">
                  <div className="flex items-start gap-3">
                    <svg className="w-6 h-6 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <div className="flex-1">
                      <h3 className="font-semibold text-blue-900 dark:text-blue-100 mb-1">
                        {nonShortlistedApplied.length} {nonShortlistedApplied.length === 1 ? 'Match Meets' : 'Matches Meet'} Your Criteria
                      </h3>
                      <p className="text-blue-800 dark:text-blue-200 text-sm mb-3">
                        These candidates match your requirements. Review and shortlist them to access their resumes.
                      </p>
                      <button
                        onClick={() => {
                          scrollToSection('applied-section');
                          handleActionModalDismiss();
                        }}
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors shadow-md hover:shadow-lg"
                      >
                        Review Applied Candidates →
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="flex gap-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={handleActionModalDismiss}
                className="flex-1 px-4 py-2 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-medium transition-colors"
              >
                Remind Me Tomorrow
              </button>
              <button
                onClick={() => setShowActionModal(false)}
                className="flex-1 px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700 text-white rounded-lg font-medium transition-all shadow-md hover:shadow-lg"
              >
                I'll Review Now
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Admin View Banner */}
        {isAdminMode && (
          <div className="mb-6 bg-amber-50 border-l-4 border-amber-400 p-4 rounded-lg shadow-md">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-amber-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3 flex-1">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-medium text-amber-800">
                      🔍 Admin View Mode
                    </h3>
                    <p className="text-sm text-amber-700 mt-1">
                      You are viewing <strong>{recruiterInfo?.name || `Recruiter #${recruiterId}`}'s</strong> dashboard as an administrator. 
                      Interactive features are disabled in this view.
                    </p>
                  </div>
                  <button
                    onClick={() => window.close()}
                    className="ml-4 bg-amber-100 hover:bg-amber-200 text-amber-800 px-3 py-1 rounded text-sm font-medium transition-colors"
                  >
                    ← Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4 mb-2">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
              {recruiterInfo ? `${recruiterInfo.name}/Dashboard` : `Recruiter Dashboard`}
            </h1>
            <div className="flex items-center gap-2 sm:gap-4">
              {/* Company Logo - Hidden on mobile */}
              {recruiterInfo && recruiterInfo.logo && (
                <div className="hidden sm:flex h-12 items-center">
                  <img 
                    src={recruiterInfo.logo.replace('/public/', '/')}
                    alt={`${recruiterInfo.organization} logo`}
                    className="h-full w-auto object-contain"
                    onError={(e) => {
                      console.error('Logo failed to load:', recruiterInfo.logo);
                      console.error('Attempted path:', recruiterInfo.logo.replace('/public/', '/'));
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
              )}
              <button
                onClick={handleLogout}
                className="px-3 sm:px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2 text-sm sm:text-base"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                <span className="hidden sm:inline">Logout</span>
              </button>
            </div>
          </div>
          <p className="text-gray-600 dark:text-gray-300">
            {recruiterInfo ? `${recruiterInfo.organization} • Manage your candidate pipeline` : 'Manage your candidate pipeline'}
            {recruiterInfo && recruiterInfo.hold_count !== undefined && (
              <span className="ml-2 text-sm">
                • <span className={`font-medium ${
                  recruiterInfo.hold_count >= recruiterInfo.hold_limit ? 'text-red-600' : 'text-blue-600'
                }`}>
                  Holds: {recruiterInfo.hold_count}/{recruiterInfo.hold_limit}
                </span>
              </span>
            )}
          </p>
          
          {/* Action Buttons - Hidden in admin mode */}
          {!isAdminMode && (
            <div className="mt-6 mb-6 flex flex-col sm:flex-row gap-3 sm:gap-4">
              <button
                onClick={() => navigate(`/recruiter/interactions/${recruiterId}`)}
                className="w-full sm:w-auto px-3 sm:px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors flex items-center justify-center sm:justify-start gap-2 text-sm sm:text-base"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span className="sm:hidden">Interaction History</span>
                <span className="hidden sm:inline">View Interaction History</span>
              </button>
              
              <button
                onClick={() => navigate('/recruiter-signup')}
                className="w-full sm:w-auto px-3 sm:px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center sm:justify-start gap-2 text-sm sm:text-base"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                <span className="sm:hidden">Update Requisition</span>
                <span className="hidden sm:inline">Update Your Requisition</span>
              </button>
              
              <button
                onClick={() => navigate('/recruiter/job-postings')}
                className="w-full sm:w-auto px-3 sm:px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center sm:justify-start gap-2 text-sm sm:text-base"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m8 0V6a2 2 0 002 2v6a2 2 0 01-2 2H8a2 2 0 01-2-2V8a2 2 0 012-2h8z" />
                </svg>
                <span className="sm:hidden">Job Postings</span>
                <span className="hidden sm:inline">Manage Job Postings</span>
              </button>
              
              <button
                onClick={() => navigate('/recruiter/dashboard/map')}
                className="w-full sm:w-auto px-3 sm:px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center justify-center sm:justify-start gap-2 text-sm sm:text-base"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                </svg>
                <span className="sm:hidden">Map View</span>
                <span className="hidden sm:inline">Geographic Map View</span>
              </button>

              <button
                onClick={() => navigate('/recruiter/faq')}
                className="w-full sm:w-auto px-3 sm:px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors flex items-center justify-center sm:justify-start gap-2 text-sm sm:text-base"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="sm:hidden">FAQ</span>
                <span className="hidden sm:inline">Dashboard FAQ</span>
              </button>
            </div>
          )}

          {/* Blog Management Section - Hidden in admin mode */}
          {!isAdminMode && !blogAccessLoading && blogAccessEnabled && (
            <div className="mt-6 mb-6">
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-4 sm:p-6 border-l-4 border-blue-500">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                      <svg className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">
                        Blog Management
                      </h3>
                      <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                        Create and manage blog posts to engage with students
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={handleBlogManagement}
                    className="w-full sm:w-auto px-3 sm:px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 text-sm sm:text-base"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                    </svg>
                    <span className="sm:hidden">Blog Posts</span>
                    <span className="hidden sm:inline">Manage Blog Posts</span>
                  </button>
                </div>
              </div>
            </div>
          )}
          
          {/* Summary Statistics */}
          {debugInfo && (
            <div className="mt-6">
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-4 sm:p-6 mb-6">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-4">Summary</h3>
                <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 text-center">
                  <div>
                    <div className="text-xl sm:text-2xl font-bold text-blue-600 dark:text-blue-400">
                      {debugInfo.actual_matches_count || 0}
                    </div>
                    <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                      Total Matches
                    </div>
                  </div>
                  <div>
                    <div className="text-xl sm:text-2xl font-bold text-green-600 dark:text-green-400">
                      {debugInfo.interested_count || 0}
                    </div>
                    <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                      Favorited You
                      <div className="hidden sm:block">(on intake form)</div>
                    </div>
                  </div>
                  <div>
                    <div className="text-xl sm:text-2xl font-bold text-yellow-600 dark:text-yellow-400">
                      {debugInfo.applied_count || 0}
                    </div>
                    <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                      Applied
                    </div>
                  </div>
                  <div>
                    <div className="text-xl sm:text-2xl font-bold text-purple-600 dark:text-purple-400">
                      {debugInfo.matched_count || 0}
                    </div>
                    <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                      Potential
                    </div>
                  </div>
                </div>
              </div>

              {/* Recruiter Requirements */}
              {debugInfo.recruiter_requirements && typeof debugInfo.recruiter_requirements === 'object' && (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-4 sm:p-6 mb-6">
                  <div
                    className="flex items-center justify-between cursor-pointer mb-4"
                    onClick={() => toggleCategoryExpanded('recruiterRequirements')}
                  >
                    <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">Recruiter Requirements</h3>
                    <button className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 text-sm">
                      {categoriesExpanded.recruiterRequirements ? '▼ Collapse' : '▶ Expand'}
                    </button>
                  </div>

                  {categoriesExpanded.recruiterRequirements && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 text-xs sm:text-sm">
                    <div>
                      <p className="font-medium text-gray-700 dark:text-gray-300">Minimum GPA:</p>
                      <p className="text-gray-600 dark:text-gray-400">{debugInfo.recruiter_requirements.minimum_gpa || 'Not specified'}</p>
                    </div>
                    <div>
                      <p className="font-medium text-gray-700 dark:text-gray-300">Preferred Class Years:</p>
                      {renderTruncatedList(debugInfo.recruiter_requirements.preferred_class_year, 'classYears', 3)}
                    </div>
                    <div>
                      <p className="font-medium text-gray-700 dark:text-gray-300">Eligible Majors:</p>
                      {renderTruncatedList(debugInfo.recruiter_requirements.eligible_majors, 'majors', 3)}
                    </div>
                    <div>
                      <p className="font-medium text-gray-700 dark:text-gray-300">Preferred Institutions:</p>
                      {renderTruncatedList(debugInfo.recruiter_requirements.preferred_institutions, 'institutions', 3)}
                    </div>
                  </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Messages */}
        {error && (
          <div className="mb-6 p-4 bg-red-100 text-red-700 rounded-md">
            {error}
          </div>
        )}
        {successMessage && (
          <div className="mb-6 p-4 bg-green-100 text-green-700 rounded-md">
            {successMessage}
          </div>
        )}

        {/* Profile Settings Section */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 sm:p-6 mb-6">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-4">Application Link Settings</h3>
          {!recruiterInfo?.external_application_link || editingLink ? (
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                Set your company's application link to send to candidates
              </p>
              <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:space-x-3">
                <input
                  type="url"
                  placeholder="https://careers.company.com/apply"
                  className="w-full sm:flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm sm:text-base"
                  value={applicationLink}
                  onChange={(e) => setApplicationLink(e.target.value)}
                />
                <div className="flex gap-2">
                  <button 
                    onClick={saveApplicationLink}
                    disabled={savingLink}
                    className={`flex-1 sm:flex-none px-3 sm:px-4 py-2 rounded-md text-white font-medium text-sm sm:text-base ${
                      savingLink 
                        ? 'bg-gray-400 cursor-not-allowed' 
                        : 'bg-blue-600 hover:bg-blue-700'
                    }`}
                  >
                    {savingLink ? 'Saving...' : 'Save'}
                  </button>
                  {editingLink && (
                    <button 
                      onClick={() => {
                        setEditingLink(false);
                        setApplicationLink('');
                      }}
                      className="flex-1 sm:flex-none px-3 sm:px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700 text-sm sm:text-base"
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">Current application link:</p>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <a 
                  href={recruiterInfo.external_application_link} 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline dark:text-blue-400 break-all text-sm sm:text-base"
                >
                  {recruiterInfo.external_application_link}
                </a>
                <button 
                  onClick={() => {
                    setEditingLink(true);
                    setApplicationLink(recruiterInfo.external_application_link);
                  }}
                  className="self-start sm:self-auto px-3 py-1 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded hover:bg-gray-50 dark:text-gray-400 dark:border-gray-600 dark:hover:bg-gray-700"
                >
                  Edit
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Sticky Navigation Bar */}
        <div className="sticky top-0 z-50 bg-gradient-to-r from-blue-600 to-blue-400 dark:from-blue-700 dark:to-blue-500 rounded-lg shadow-lg mb-6 px-4 sm:px-6 py-3">
          <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide">
            {/* Shortlisted Section */}
            {shortlistedIds.size > 0 && (
              <button
                onClick={() => scrollToSection('shortlisted-section')}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg whitespace-nowrap text-sm font-medium transition-all ${
                  activeSection === 'shortlisted'
                    ? 'bg-gradient-to-r from-purple-500 via-purple-600 to-pink-600 text-white shadow-lg'
                    : 'bg-white/90 hover:bg-white text-gray-700 hover:shadow-md'
                }`}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                </svg>
                <span className="hidden sm:inline">Shortlisted</span>
                <span className="inline sm:hidden">Short</span>
                <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                  activeSection === 'shortlisted'
                    ? 'bg-white/20 text-white'
                    : 'bg-white dark:bg-gray-900 text-purple-600 dark:text-purple-400'
                }`}>
                  {shortlistedIds.size}
                </span>
              </button>
            )}

            {/* Favorited You Section */}
            {debugInfo?.interested_count > 0 && (
              <button
                onClick={() => scrollToSection('favorited-you-section')}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg whitespace-nowrap text-sm font-medium transition-all ${
                  activeSection === 'favorited'
                    ? 'bg-green-500 text-white shadow-lg'
                    : 'bg-white/90 hover:bg-white text-gray-700 hover:shadow-md'
                }`}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
                <span className="hidden sm:inline">Favorited You</span>
                <span className="inline sm:hidden">Fav</span>
                <span className="px-2 py-0.5 bg-white dark:bg-gray-900 text-green-600 dark:text-green-400 rounded-full text-xs font-bold">
                  {debugInfo.interested_count}
                </span>
              </button>
            )}

            {/* Applied Section */}
            {debugInfo?.applied_count > 0 && (
              <button
                onClick={() => scrollToSection('applied-section')}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg whitespace-nowrap text-sm font-medium transition-all ${
                  activeSection === 'applied'
                    ? 'bg-blue-500 text-white shadow-lg'
                    : 'bg-white/90 hover:bg-white text-gray-700 hover:shadow-md'
                }`}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span className="hidden sm:inline">Applied</span>
                <span className="inline sm:hidden">App</span>
                <span className="px-2 py-0.5 bg-white dark:bg-gray-900 text-blue-600 dark:text-blue-400 rounded-full text-xs font-bold">
                  {debugInfo.applied_count}
                </span>
              </button>
            )}

            {/* Matching Candidates Section */}
            {debugInfo?.matched_count > 0 && (
              <button
                onClick={() => scrollToSection('matching-section')}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg whitespace-nowrap text-sm font-medium transition-all ${
                  activeSection === 'matching'
                    ? 'bg-purple-500 text-white shadow-lg'
                    : 'bg-white/90 hover:bg-white text-gray-700 hover:shadow-md'
                }`}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                <span className="hidden sm:inline">Matching</span>
                <span className="inline sm:hidden">Match</span>
                <span className="px-2 py-0.5 bg-white dark:bg-gray-900 text-purple-600 dark:text-purple-400 rounded-full text-xs font-bold">
                  {debugInfo.matched_count}
                </span>
              </button>
            )}

            {/* Divider */}
            <div className="hidden sm:block h-8 w-px bg-white/30 mx-2"></div>

            {/* Back to Top Button */}
            <button
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="flex items-center gap-2 px-3 py-2 rounded-lg whitespace-nowrap text-sm font-medium bg-white/90 hover:bg-white text-gray-700 hover:shadow-md transition-all ml-auto"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
              </svg>
              <span className="hidden sm:inline">Top</span>
            </button>
          </div>
        </div>

        {/* Shortlisted Students Section */}
        {shortlistedIds.size > 0 && (
          <div id="shortlisted-section" className="mb-8">
            <div
              className="flex items-center justify-between cursor-pointer mb-4"
              onClick={() => toggleCategoryExpanded('shortlisted')}
            >
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">
                Shortlisted Candidates ({shortlistedIds.size})
              </h2>
              <button className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
                {categoriesExpanded.shortlisted ? '▼ Collapse' : '▶ Expand'}
              </button>
            </div>

            {categoriesExpanded.shortlisted && (
              <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-4">
              {/* Group 1: Favorited You */}
              {interestedStudents.filter(s => shortlistedIds.has(s.id)).length > 0 && (
                <div id="favorited-section" className="mb-6">
                  <h3 className="text-lg font-medium text-gray-800 dark:text-gray-200 mb-3">
                    Favorited You ({interestedStudents.filter(s => shortlistedIds.has(s.id)).length})
                  </h3>

                  {/* Students who favorited AND applied - Completed Application */}
                  {interestedStudents
                    .filter(s => shortlistedIds.has(s.id))
                    .filter(interested => appliedStudents.some(applied => applied.id === interested.id))
                    .length > 0 && (
                    <div className="mb-4">
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        ✅ Completed Application ({interestedStudents
                          .filter(s => shortlistedIds.has(s.id))
                          .filter(interested => appliedStudents.some(applied => applied.id === interested.id))
                          .length})
                      </h4>
                      {interestedStudents
                        .filter(s => shortlistedIds.has(s.id))
                        .filter(interested => appliedStudents.some(applied => applied.id === interested.id))
                        .sort((a, b) => `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`))
                        .map(student => renderStudent(student, 'interested'))}
                    </div>
                  )}

                  {/* Students who favorited but did NOT apply - Follow-up needed */}
                  {interestedStudents
                    .filter(s => shortlistedIds.has(s.id))
                    .filter(interested => !appliedStudents.some(applied => applied.id === interested.id))
                    .length > 0 && (
                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                      <h4 className="text-sm font-medium text-red-700 dark:text-red-300 mb-2">
                        🔴 Follow-up Needed - Application Not Completed ({interestedStudents
                          .filter(s => shortlistedIds.has(s.id))
                          .filter(interested => !appliedStudents.some(applied => applied.id === interested.id))
                          .length})
                      </h4>
                      {interestedStudents
                        .filter(s => shortlistedIds.has(s.id))
                        .filter(interested => !appliedStudents.some(applied => applied.id === interested.id))
                        .sort((a, b) => `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`))
                        .map(student => renderStudent(student, 'interested'))}
                    </div>
                  )}
                </div>
              )}

              {/* Group 2: Applied Students - Only show those who didn't favorite */}
              {appliedStudents
                .filter(s => shortlistedIds.has(s.id))
                .filter(applied => !interestedStudents.some(interested => interested.id === applied.id))
                .length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-medium text-gray-800 dark:text-gray-200 mb-3">
                    Applied Students ({appliedStudents
                      .filter(s => shortlistedIds.has(s.id))
                      .filter(applied => !interestedStudents.some(interested => interested.id === applied.id))
                      .length})
                  </h3>
                  {appliedStudents
                    .filter(s => shortlistedIds.has(s.id))
                    .filter(applied => !interestedStudents.some(interested => interested.id === applied.id))
                    .sort((a, b) => `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`))
                    .map(student => renderStudent(student, 'applied'))}
                </div>
              )}

              {/* Group 3: Matched but Not Applied */}
              {matchedStudents.filter(s => shortlistedIds.has(s.id)).length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-medium text-gray-800 dark:text-gray-200 mb-3">
                    Potential Candidates (no NACME app yet) ({matchedStudents.filter(s => shortlistedIds.has(s.id)).length})
                  </h3>
                  {matchedStudents
                    .filter(s => shortlistedIds.has(s.id))
                    .sort((a, b) => a.id - b.id)
                    .map(student => renderStudent(student, 'matched'))}
                </div>
              )}
              </div>
            )}
          </div>
        )}

        {/* All Students Sections */}
        {/* Group 1: Favorited You - Only show if there are students who favorited */}
        {(interestedStudents.length > 0 || paginationMeta.interested_total > 0) && (() => {
          // Server-side pagination: show all loaded students, more load on scroll
          const visibleInterestedStudents = interestedStudents;
          const hasMoreInterestedStudents = paginationMeta.has_more_interested;

          return (
            <div id="favorited-you-section" className="mb-8">
              <div
                className="flex items-center justify-between cursor-pointer mb-4"
                onClick={() => toggleCategoryExpanded('interested')}
              >
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  Favorited You ({paginationMeta.interested_total || interestedStudents.length})
                </h2>
                <button className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
                  {categoriesExpanded.interested ? '▼ Collapse' : '▶ Expand'}
                </button>
              </div>

              {categoriesExpanded.interested && (
                <>
                {/* Notice for Favorited Students */}
                <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                  <div className="flex items-start gap-3">
                    <svg className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div>
                      <p className="text-blue-800 dark:text-blue-200 font-medium">
                        💡 How to engage potential candidates:
                      </p>
                      <p className="text-blue-700 dark:text-blue-300 text-sm mt-1">
                        You're in the Top Companies List for these candidates! These students have completed a NACME Corporate Scholarship Application. Shortlist candidates, then send them your company's link, invite them to complete their full application, and add them to your interview queue.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Students who favorited AND applied - Completed Application */}
                {visibleInterestedStudents.filter(interested =>
                  appliedStudents.some(applied => applied.id === interested.id)
                ).length > 0 && (
                  <div className="mb-4">
                    <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      ✅ Completed Application ({interestedStudents.filter(interested =>
                        appliedStudents.some(applied => applied.id === interested.id)
                      ).length})
                    </h3>

                    {/* New Candidates (not shortlisted yet) */}
                    {visibleInterestedStudents
                      .filter(interested =>
                        appliedStudents.some(applied => applied.id === interested.id) &&
                        !shortlistedIds.has(interested.id)
                      ).length > 0 && (
                      <div className="mb-3">
                        <h4 className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2 uppercase tracking-wide">
                          New Candidates ({interestedStudents.filter(interested =>
                            appliedStudents.some(applied => applied.id === interested.id) &&
                            !shortlistedIds.has(interested.id)
                          ).length})
                        </h4>
                        {visibleInterestedStudents
                          .filter(interested =>
                            appliedStudents.some(applied => applied.id === interested.id) &&
                            !shortlistedIds.has(interested.id)
                          )
                          .sort((a, b) => `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`))
                          .map(student => renderStudent(student, 'interested'))}
                      </div>
                    )}

                    {/* Already Shortlisted */}
                    {visibleInterestedStudents
                      .filter(interested =>
                        appliedStudents.some(applied => applied.id === interested.id) &&
                        shortlistedIds.has(interested.id)
                      ).length > 0 && (
                      <div>
                        <h4 className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2 uppercase tracking-wide">
                          Already Shortlisted ({interestedStudents.filter(interested =>
                            appliedStudents.some(applied => applied.id === interested.id) &&
                            shortlistedIds.has(interested.id)
                          ).length})
                        </h4>
                        {visibleInterestedStudents
                          .filter(interested =>
                            appliedStudents.some(applied => applied.id === interested.id) &&
                            shortlistedIds.has(interested.id)
                          )
                          .sort((a, b) => `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`))
                          .map(student => renderStudent(student, 'interested'))}
                      </div>
                    )}
                  </div>
                )}

                {/* Students who favorited but did NOT apply - Follow-up needed */}
                {visibleInterestedStudents.filter(interested =>
                  !appliedStudents.some(applied => applied.id === interested.id)
                ).length > 0 && (
                  <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                    <h3 className="text-sm font-medium text-red-700 dark:text-red-300 mb-2">
                      🔴 Follow-up Needed - Application Not Completed ({interestedStudents.filter(interested =>
                        !appliedStudents.some(applied => applied.id === interested.id)
                      ).length})
                    </h3>

                    {/* New Candidates (not shortlisted yet) */}
                    {visibleInterestedStudents
                      .filter(interested =>
                        !appliedStudents.some(applied => applied.id === interested.id) &&
                        !shortlistedIds.has(interested.id)
                      ).length > 0 && (
                      <div className="mb-3">
                        <h4 className="text-xs font-semibold text-red-600 dark:text-red-400 mb-2 uppercase tracking-wide">
                          New Candidates ({interestedStudents.filter(interested =>
                            !appliedStudents.some(applied => applied.id === interested.id) &&
                            !shortlistedIds.has(interested.id)
                          ).length})
                        </h4>
                        {visibleInterestedStudents
                          .filter(interested =>
                            !appliedStudents.some(applied => applied.id === interested.id) &&
                            !shortlistedIds.has(interested.id)
                          )
                          .sort((a, b) => `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`))
                          .map(student => renderStudent(student, 'interested'))}
                      </div>
                    )}

                    {/* Already Shortlisted */}
                    {visibleInterestedStudents
                      .filter(interested =>
                        !appliedStudents.some(applied => applied.id === interested.id) &&
                        shortlistedIds.has(interested.id)
                      ).length > 0 && (
                      <div>
                        <h4 className="text-xs font-semibold text-red-600 dark:text-red-400 mb-2 uppercase tracking-wide">
                          Already Shortlisted ({interestedStudents.filter(interested =>
                            !appliedStudents.some(applied => applied.id === interested.id) &&
                            shortlistedIds.has(interested.id)
                          ).length})
                        </h4>
                        {visibleInterestedStudents
                          .filter(interested =>
                            !appliedStudents.some(applied => applied.id === interested.id) &&
                            shortlistedIds.has(interested.id)
                          )
                          .sort((a, b) => `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`))
                          .map(student => renderStudent(student, 'interested'))}
                      </div>
                    )}
                  </div>
                )}

                {/* Infinite Scroll Sentinel for Favorited You Section */}
                {hasMoreInterestedStudents && (
                  <div ref={interestedSentinelRef} className="flex items-center justify-center py-8">
                    <div className="flex items-center gap-2 text-blue-600 dark:text-blue-400">
                      <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>Loading more candidates...</span>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
          );
        })()}

        {/* Group 2: Additional Applicants who meet your criteria */}
        <div id="applied-section" className="mb-8">
          <div
            className="flex items-center justify-between cursor-pointer mb-4"
            onClick={() => toggleCategoryExpanded('applied')}
          >
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Additional Applicants who meet your criteria ({appliedStudents.filter(applied =>
                !interestedStudents.some(interested => interested.id === applied.id)
              ).length})
            </h2>
            <button className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
              {categoriesExpanded.applied ? '▼ Collapse' : '▶ Expand'}
            </button>
          </div>

          {categoriesExpanded.applied && (
            <>
              {/* Notice for Additional Applicants */}
              <div className="mb-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                <div className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <p className="text-green-800 dark:text-green-200 font-medium">
                      💡 How to engage potential candidates:
                    </p>
                    <p className="text-green-700 dark:text-green-300 text-sm mt-1">
                      These students have completed a NACME Corporate Scholarship Application. Shortlist candidates, then send them your company's link and invite them to complete their full application!
                    </p>
                  </div>
                </div>
              </div>

              {(() => {
                // Filter applied students who didn't favorite (overlap removed)
                const appliedNotFavorited = appliedStudents.filter(applied =>
                  !interestedStudents.some(interested => interested.id === applied.id)
                );

                // Server-side pagination: show all loaded students, more load on scroll
                const visibleAppliedStudents = appliedNotFavorited;
                const hasMoreAppliedStudents = paginationMeta.has_more_applied;

                if (appliedNotFavorited.length === 0 && !paginationMeta.has_more_applied) {
                  return <p className="text-gray-500 dark:text-gray-400">No shortlisted students have applied without favoriting you.</p>;
                }

                return (
                  <>
                    {visibleAppliedStudents
                      .sort((a, b) => `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`))
                      .map(student => renderStudent(student, 'applied'))}

                    {/* Infinite Scroll Sentinel for Applied Section */}
                    {hasMoreAppliedStudents && (
                      <div ref={appliedSentinelRef} className="flex items-center justify-center py-8">
                        <div className="flex items-center gap-2 text-green-600 dark:text-green-400">
                          <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          <span>Loading more candidates...</span>
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
            </>
          )}
        </div>

        {/* Group 3: Matched but Not Applied */}
        <div id="matching-section" className="mb-8">
          <div
            className="flex items-center justify-between cursor-pointer mb-4"
            onClick={() => toggleCategoryExpanded('matched')}
          >
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6v1a2 2 0 01-2 2H5a2 2 0 01-2-2zm9-13.5V9" />
              </svg>
              Matching Candidates ({paginationMeta.matched_total || matchedStudents.length})
            </h2>
            <button className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
              {categoriesExpanded.matched ? '▼ Collapse' : '▶ Expand'}
            </button>
          </div>
          
          {categoriesExpanded.matched && (
            <>
              {/* Notice for Matching Candidates */}
              <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                <div className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <p className="text-blue-800 dark:text-blue-200 font-medium">
                      💡 How to engage potential candidates:
                    </p>
                    <p className="text-blue-700 dark:text-blue-300 text-sm mt-1">
                      Shortlist candidates and click "Request Student Profile" to encourage students to update their profile information!
                    </p>
                  </div>
                </div>
              </div>

              {(() => {
                // Server-side pagination: show all loaded students, more load on scroll
                const visibleMatchedStudents = matchedStudents;
                const hasMoreMatchedStudents = paginationMeta.has_more_matched;

                if (matchedStudents.length === 0 && paginationMeta.matched_total === 0) {
                  return <p className="text-gray-500 dark:text-gray-400">No matching candidates found.</p>;
                }

                return (
                  <>
                    {visibleMatchedStudents
                      .sort((a, b) => a.id - b.id)
                      .map(student => renderStudent(student, 'matched'))}

                    {/* Infinite Scroll Sentinel for Matching Section */}
                    {hasMoreMatchedStudents && (
                      <div ref={matchedSentinelRef} className="flex items-center justify-center py-8">
                        <div className="flex items-center gap-2 text-blue-600 dark:text-blue-400">
                          <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          <span>Loading more candidates...</span>
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
            </>
          )}
        </div>
      </div>

      {/* Make Offer Modal */}
      {showOfferModal && selectedStudentForOffer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 sm:p-6 max-w-2xl w-full max-h-[85vh] overflow-y-auto">
            <h3 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white mb-4">
              <span className="sm:hidden">Make Offer</span>
              <span className="hidden sm:inline">Make Offer to {selectedStudentForOffer.first_name} {selectedStudentForOffer.last_name}</span>
            </h3>
            
            <div className="space-y-3 sm:space-y-4">
              {/* Position Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Position Title *
                </label>
                <input
                  type="text"
                  placeholder="Software Engineering Intern"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm sm:text-base"
                  value={offerData.position_title}
                  onChange={(e) => handleOfferInputChange('position_title', e.target.value)}
                />
              </div>

              {/* Position Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Position Description
                </label>
                <textarea
                  rows="2"
                  placeholder="Brief description of the role, responsibilities, and requirements..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm sm:text-base"
                  value={offerData.position_description}
                  onChange={(e) => handleOfferInputChange('position_description', e.target.value)}
                />
              </div>

              {/* Compensation */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Compensation
                </label>
                <input
                  type="text"
                  placeholder="$25/hour or $5,000/month"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm sm:text-base"
                  value={offerData.compensation}
                  onChange={(e) => handleOfferInputChange('compensation', e.target.value)}
                />
              </div>

              {/* Date Fields Row */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Start Date
                  </label>
                  <input
                    type="date"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm sm:text-base"
                    value={offerData.start_date}
                    onChange={(e) => handleOfferInputChange('start_date', e.target.value)}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Duration
                  </label>
                  <input
                    type="text"
                    placeholder="12 weeks"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm sm:text-base"
                    value={offerData.duration}
                    onChange={(e) => handleOfferInputChange('duration', e.target.value)}
                  />
                </div>
              </div>

              {/* Response Deadline */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Response Deadline
                </label>
                <input
                  type="date"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm sm:text-base"
                  value={offerData.response_deadline}
                  onChange={(e) => handleOfferInputChange('response_deadline', e.target.value)}
                />
              </div>

              {/* Custom Message */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Personal Message (Optional)
                </label>
                <textarea
                  rows="2"
                  placeholder="We're excited to have you join our team..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm sm:text-base"
                  value={offerData.custom_message}
                  onChange={(e) => handleOfferInputChange('custom_message', e.target.value)}
                />
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row justify-end gap-3 mt-6">
              <button 
                onClick={closeOfferModal}
                className="w-full sm:w-auto px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700 text-sm sm:text-base"
              >
                Cancel
              </button>
              <button 
                onClick={sendOffer}
                disabled={sendingOffer || !offerData.position_title.trim()}
                className={`w-full sm:w-auto px-4 sm:px-6 py-2 rounded-md text-white font-medium text-sm sm:text-base ${
                  sendingOffer || !offerData.position_title.trim()
                    ? 'bg-gray-400 cursor-not-allowed' 
                    : 'bg-orange-600 hover:bg-orange-700'
                }`}
              >
                {sendingOffer ? 'Sending...' : '🎯 Send Offer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Application Link Required Modal */}
      {showLinkModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Application Link Required
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              You need to set your company's application link before you can send external links to candidates. 
              This link will be sent to students when you click "Send External Link".
            </p>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Company Application Link
              </label>
              <input
                type="url"
                placeholder="https://careers.company.com/apply"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                value={tempApplicationLink}
                onChange={(e) => setTempApplicationLink(e.target.value)}
              />
            </div>
            
            <div className="flex justify-end space-x-3">
              <button 
                onClick={() => {
                  setShowLinkModal(false);
                  setTempApplicationLink('');
                  setPendingStudentId(null);
                }}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button 
                onClick={saveAndContinue}
                disabled={savingLink}
                className={`px-4 py-2 rounded-md text-white font-medium ${
                  savingLink 
                    ? 'bg-gray-400 cursor-not-allowed' 
                    : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                {savingLink ? 'Saving...' : 'Save & Continue'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default RecruiterDashboard;